import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const formatApiErrorDetail = (detail) => {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
};

const SettingsForm = ({ introTitle, introContent, onTitleChange, onContentChange }) => (
  <>
    <div className="space-y-2">
      <Label htmlFor="introTitle" className="text-slate-300">Introduction Title</Label>
      <Input
        id="introTitle"
        value={introTitle}
        onChange={(e) => onTitleChange(e.target.value)}
        placeholder="Welcome to UK Discovered"
        className="bg-slate-800 border-slate-600 text-white placeholder:text-slate-500"
      />
    </div>
    <div className="space-y-2">
      <Label htmlFor="introContent" className="text-slate-300">Introduction Content</Label>
      <Textarea
        id="introContent"
        value={introContent}
        onChange={(e) => onContentChange(e.target.value)}
        placeholder="Write your site introduction..."
        className="bg-slate-800 border-slate-600 text-white placeholder:text-slate-500 min-h-[150px]"
      />
    </div>
  </>
);

const SettingsModal = ({ isOpen, onClose, onSave, settings }) => {
  const [introTitle, setIntroTitle] = useState("");
  const [introContent, setIntroContent] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (settings) {
      setIntroTitle(settings.intro_title || "");
      setIntroContent(settings.intro_content || "");
    }
  }, [settings, isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);
    try {
      await onSave({ intro_title: introTitle, intro_content: introContent });
      onClose();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl bg-slate-900 border-slate-700 text-white">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-white">Edit Site Introduction</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-5 pt-4">
          {error && (
            <div className="bg-red-500/20 border border-red-500 text-red-400 px-4 py-3 rounded-lg text-sm">{error}</div>
          )}
          <SettingsForm introTitle={introTitle} introContent={introContent} onTitleChange={setIntroTitle} onContentChange={setIntroContent} />
          <div className="flex gap-3 pt-2">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1 border-slate-600 text-slate-300 hover:bg-slate-800">Cancel</Button>
            <Button type="submit" disabled={isLoading} className="flex-1 bg-blue-600 hover:bg-blue-700 text-white">
              {isLoading ? "Saving..." : "Save"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default SettingsModal;
